import React, { useEffect, useRef, useState } from 'react';
import { PaperPlaneTilt, X, Sparkle, Lightning, Buildings, ArrowsClockwise, UsersThree } from '@phosphor-icons/react';
import axios from 'axios';
import { LuminexIcon } from './LuminexLogo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Suggested quick actions shown as chips on first turn
const QUICK_PROMPTS = [
  { label: 'What can I do here?', icon: <Sparkle size={12} weight="duotone" />, msg: 'What can I do here?' },
  { label: 'Explain Re-engineering', icon: <ArrowsClockwise size={12} weight="duotone" />, msg: 'Can you briefly explain what Re-engineering does?' },
  { label: 'Show me the Library', icon: <Buildings size={12} weight="duotone" />, msg: 'How do I browse the industry Operating Models?' },
  { label: 'How do I invite my team?', icon: <UsersThree size={12} weight="duotone" />, msg: 'How do I invite my team to the workspace?' },
];

function authHeader() {
  const t = localStorage.getItem('capitalops_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function WelcomeChatOverlay({ user, onClose }) {
  const firstName = user?.firstName || 'there';
  const greeting = `Hi ${firstName}, I'm Vidhā — your process intelligence guide. Take a breath. When you're ready, ask me anything about the platform, or jump into what you came here to do.`;
  const [messages, setMessages] = useState([{ role: 'assistant', content: greeting }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [closing, setClosing] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput('');
    const nextMessages = [...messages, { role: 'user', content: msg }];
    setMessages(nextMessages);
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/ai/welcome-chat`,
        { message: msg, history: nextMessages.slice(-10) },
        { headers: authHeader() }
      );
      setMessages([...nextMessages, { role: 'assistant', content: data.reply || '(no reply)' }]);
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      const low = /credit balance is too low|universal key/i.test(detail);
      const friendly = low
        ? "I'm a little quiet right now — my AI credits need topping up. You can still explore the platform; use Skip to continue. (Admin → Profile → Universal Key → Add Balance.)"
        : "I couldn't reach the AI service. Please try again in a moment, or use Skip to continue.";
      setMessages([...nextMessages, { role: 'assistant', content: friendly }]);
    } finally {
      setLoading(false);
    }
  };

  const dismiss = () => {
    setClosing(true);
    sessionStorage.setItem('vidhana_welcome_dismissed', '1');
    setTimeout(() => onClose?.(), 180);
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div
      data-testid="welcome-chat-overlay"
      style={{
        position: 'fixed', inset: 0, zIndex: 90,
        background: 'rgba(10,14,26,0.65)',
        backdropFilter: 'blur(8px)',
        display: 'grid', placeItems: 'center',
        padding: 24,
        opacity: closing ? 0 : 1,
        transition: 'opacity 180ms ease',
      }}
    >
      <div
        style={{
          width: '100%', maxWidth: 560,
          background: 'var(--bg-1)',
          border: '1px solid var(--border-1)',
          borderRadius: 16,
          boxShadow: '0 24px 64px -16px rgba(10,14,26,0.35)',
          overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
          maxHeight: '82vh',
          transform: closing ? 'translateY(8px)' : 'translateY(0)',
          transition: 'transform 180ms ease',
        }}
      >
        {/* Header — soothing ember glow */}
        <div style={{
          position: 'relative',
          padding: '22px 28px 18px',
          background: 'linear-gradient(135deg, rgba(30,78,140,0.08) 0%, rgba(242,107,31,0.06) 100%)',
          borderBottom: '1px solid var(--border-1)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: 'linear-gradient(135deg, #143A6B 0%, #1E4E8C 60%, #2E70AD 100%)',
              display: 'grid', placeItems: 'center',
              color: '#F3F4F6',
              boxShadow: '0 4px 14px -4px rgba(30,78,140,0.5)',
            }}>
              <LuminexIcon size={22} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.01em' }}>
                Vidhā <span style={{ color: 'var(--text-3)', fontSize: 13, fontWeight: 400, marginLeft: 6 }}>विधा</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 2 }}>Your process intelligence guide · online</div>
            </div>
            <button onClick={dismiss} data-testid="welcome-skip-btn-top"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', padding: 6 }}
              title="Skip for now"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div ref={scrollRef} data-testid="welcome-messages"
          style={{ flex: 1, overflowY: 'auto', padding: '18px 24px 8px', minHeight: 220 }}>
          {messages.map((m, i) => (
            <div key={i} style={{ display: 'flex', marginBottom: 14, justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
              <div style={{
                maxWidth: '85%',
                padding: '10px 14px',
                borderRadius: m.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                fontSize: 13.5, lineHeight: 1.55,
                background: m.role === 'user' ? 'var(--accent)' : 'var(--bg-2)',
                color: m.role === 'user' ? '#F3F4F6' : 'var(--text-1)',
                whiteSpace: 'pre-wrap',
              }}>
                {m.content}
              </div>
            </div>
          ))}
          {loading && (
            <div style={{ display: 'flex', marginBottom: 14 }}>
              <div style={{ padding: '12px 14px', borderRadius: '14px 14px 14px 4px', background: 'var(--bg-2)', display: 'flex', gap: 4, alignItems: 'center' }}>
                <span className="vidha-dot" style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--text-3)', animation: 'vidhaDot 1.2s infinite ease-in-out' }} />
                <span className="vidha-dot" style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--text-3)', animation: 'vidhaDot 1.2s infinite ease-in-out 0.15s' }} />
                <span className="vidha-dot" style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--text-3)', animation: 'vidhaDot 1.2s infinite ease-in-out 0.3s' }} />
              </div>
            </div>
          )}
        </div>

        {/* Quick prompt chips (only while conversation is short) */}
        {messages.length <= 2 && !loading && (
          <div style={{ padding: '0 24px 12px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {QUICK_PROMPTS.map(qp => (
              <button key={qp.label} onClick={() => send(qp.msg)}
                data-testid={`welcome-quick-${qp.label.replace(/\s+/g,'-').toLowerCase()}`}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '6px 12px', fontSize: 11, fontWeight: 600,
                  background: 'var(--bg-2)', border: '1px solid var(--border-1)',
                  borderRadius: 100, color: 'var(--text-1)', cursor: 'pointer',
                }}>
                {qp.icon} {qp.label}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div style={{ padding: '14px 20px 18px', borderTop: '1px solid var(--border-1)', background: 'var(--bg-0)' }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
            <textarea
              ref={inputRef}
              data-testid="welcome-chat-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={onKey}
              rows={1}
              placeholder="Ask Vidhā anything about the platform…"
              style={{
                flex: 1, resize: 'none', minHeight: 40, maxHeight: 120,
                padding: '10px 12px', fontSize: 13, fontFamily: 'inherit',
                background: 'var(--bg-1)', color: 'var(--text-1)',
                border: '1px solid var(--border-1)', borderRadius: 10,
                outline: 'none',
              }}
            />
            <button onClick={() => send()} disabled={loading || !input.trim()}
              data-testid="welcome-chat-send-btn"
              className="btn btn-primary"
              style={{ padding: '10px 14px', opacity: (loading || !input.trim()) ? 0.55 : 1 }}
            >
              {loading ? <Lightning size={14} weight="duotone" /> : <PaperPlaneTilt size={14} weight="duotone" />}
            </button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
            <span style={{ fontSize: 10, color: 'var(--text-3)' }}>Powered by Generative AI · replies are suggestions, not advice.</span>
            <button onClick={dismiss} data-testid="welcome-skip-btn"
              style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
              Skip for now
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes vidhaDot {
          0%, 80%, 100% { opacity: 0.25; transform: translateY(0); }
          40% { opacity: 1; transform: translateY(-3px); }
        }
      `}</style>
    </div>
  );
}
